import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useContext,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import {
  selectSelectedBlacklineSegment,
  setSelectedBlacklineSegment,
  setShowRedline,
} from '../../../redux/viewer-slice'
import DocumentReference from '../../document-reference'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { useBackButton } from '../../../hooks/use-back-button'
import DiffCommentComponent from '../../overlay/diff-comment-component'
import {
  DocumentBlackline,
  DocumentBlacklineSegment,
} from '../../../shared/interfaces/project/document/blackline/document-blackline.interface'
import DiffCreateComment from '../../overlay/diff-create-comment'
import { DocumentViewerContext } from '../../../contexts/document-viewer-instance-context'

interface BlacklineCommentCardProps {
  segment: DocumentBlacklineSegment
}

const BlacklineCommentTableCard: React.FC<BlacklineCommentCardProps> = ({
  segment,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const documentViewerContext = useContext(DocumentViewerContext)
  const { documentViewer } = documentViewerContext

  const selectedBlacklineSegment = useSelector(selectSelectedBlacklineSegment)

  const isSelectedSegment = useMemo(() => {
    return selectedBlacklineSegment?.id === segment.id
  }, [selectedBlacklineSegment, segment])

  const [searchParams, setSearchParams] = useSearchParams()
  const [commentsOpen, setCommentsOpen] = useState<boolean>(false)

  const { addToNavigationHistoryAndNavigate } = useBackButton()

  const dispatch = useDispatch()

  const onClickDocumentReference = useCallback(() => {
    addToNavigationHistoryAndNavigate(
      'comments',
      (segment.document_blackline as DocumentBlackline).document?.uuid,
      segment.page
    )
    dispatch(setShowRedline(true))
    searchParams.set('page', segment.page.toString())
    setSearchParams(searchParams)
    dispatch(setSelectedBlacklineSegment(segment))
    documentViewer?.setCurrentPage(segment.page, false)
  }, [
    addToNavigationHistoryAndNavigate,
    dispatch,
    documentViewer,
    searchParams,
    segment,
    setSearchParams,
  ])

  const onCommentsClick = useCallback(() => {
    setCommentsOpen(!commentsOpen)
  }, [commentsOpen])

  const remainingCommentCount = useMemo(() => {
    if (!segment.comments?.length) {
      return 0
    }
    if (segment?.comments?.length >= 0 && segment?.comments?.length <= 2) {
      return 0
    }
    return segment?.comments?.length - 2
  }, [segment])

  return (
    <div
      ref={ref}
      className={`flex flex-col items-stretch rounded border ${isSelectedSegment && 'border-l-2 border-l-gray-500'} bg-white`}
    >
      <div className="flex items-center justify-between border-b p-2"></div>
      <div className="flex justify-between gap-2 p-2 text-xs text-gray-400">
        <span className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
          <svg
            className="h-1.5 w-1.5 fill-gray-500"
            viewBox="0 0 6 6"
            aria-hidden="true"
          >
            <circle cx={3} cy={3} r={3} />
          </svg>
          Redline comment
        </span>
        <DocumentReference
          onClick={onClickDocumentReference}
          documentTitle={
            (segment.document_blackline as DocumentBlackline).document?.title
          }
          pageNumber={segment.page ?? null}
        />
      </div>

      <div className="m-1 mx-2 space-y-2 border-t py-1 text-sm">
        {segment?.comments?.[0] ? (
          <DiffCommentComponent
            documentBlacklineSegmentComment={segment?.comments?.[0]}
            segment={segment}
          />
        ) : null}
        {remainingCommentCount > 0 ? (
          <button
            data-cy="blackline-comments-expand-button"
            onClick={onCommentsClick}
            className={
              'flex w-full items-center justify-start gap-1 border-t p-1 px-2 text-xs text-gray-500 hover:bg-gray-100'
            }
          >
            <span>
              {commentsOpen ? 'Hide' : 'Show'} ({remainingCommentCount})
              Comments
            </span>
            {commentsOpen ? (
              <ChevronUpIcon width={18} />
            ) : (
              <ChevronDownIcon width={18} />
            )}
          </button>
        ) : null}
        {commentsOpen &&
          segment?.comments?.map((comment, index) => {
            if (index === 0 || index === (segment?.comments?.length ?? 0) - 1) {
              return null
            }
            return (
              <DiffCommentComponent
                key={comment.id}
                documentBlacklineSegmentComment={comment}
                segment={segment}
              />
            )
          })}
        {segment?.comments?.[segment?.comments?.length - 1] &&
        segment?.comments?.length >= 2 ? (
          <DiffCommentComponent
            documentBlacklineSegmentComment={
              segment?.comments?.[segment?.comments?.length - 1]
            }
            segment={segment}
          />
        ) : null}
        {segment && segment.id && <DiffCreateComment segment={segment} />}
      </div>
    </div>
  )
}

export default BlacklineCommentTableCard
